import { forwardRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import classnames from "classnames"

import Button from "components/Button"
import IconButton from "components/IconButton"
import Modal from "components/Modality/Modal"

import styles from "./Dialog.module.scss"

type DialogProps = {
  className?: string
  variant?: any
  title?: any
  caption?: any
  children?: any
  onClose?: any
  onOK?: any
  onCancel?: any
  withCancel?: any
  cancelLabel?: any
  okayLabel?: any
  inert?: any
  shouldKeepOpenOnOK?: any
  shouldKeepOpenOnCancel?: any
}

const Dialog = (
  {
    className,
    variant,
    title,
    caption,
    children,
    onClose,
    onOK,
    onCancel,
    withCancel,
    cancelLabel,
    okayLabel,
    inert,
    shouldKeepOpenOnOK,
    shouldKeepOpenOnCancel,
  }: DialogProps,
  ref: any,
) => {
  const { t } = useTranslation("label")

  const closeModal = useCallback(
    (event: any) => {
      if (event?.target?.value === "okay" && typeof onOK === "function") {
        onOK()
        if (shouldKeepOpenOnOK) return
      }
      if (event?.target?.value === "cancel" && typeof onCancel === "function") {
        onCancel()
        if (shouldKeepOpenOnCancel) return
      }
      ref.current.close()
    },
    [onOK, onCancel, ref, shouldKeepOpenOnOK, shouldKeepOpenOnCancel],
  )

  return (
    <Modal ref={ref} className={classnames(styles.container, styles[variant], className)} onClose={onClose}>
      <article className={styles.dialog}>
        <header className={styles.header}>
          <h2>{title}</h2>
          {inert ? null : <IconButton variant="bare" icon="x" onClick={closeModal} title={t("close")} />}
        </header>
        {caption ? <h3 className={styles.caption}>{caption}</h3> : null}
        <div className={styles.content}>{children}</div>
        {inert ? null : (
          <footer className={styles.actions}>
            {withCancel ? (
              <Button variant="secondary" value="cancel" onClick={closeModal}>
                {cancelLabel || t("cancel")}
              </Button>
            ) : null}
            <Button variant="primary" value="okay" onClick={closeModal}>
              {okayLabel && okayLabel !== "undefined" ? okayLabel : t("okay")}
            </Button>
          </footer>
        )}
      </article>
    </Modal>
  )
}

export default forwardRef(Dialog)
